import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Box, CircularProgress, FormControl, InputLabel, Link, MenuItem, Select } from '@mui/material';
import { useEffect, useState } from 'react';
import { useRootStore } from '../hooks/useRootStore';
import { Skill } from '../data/Skill';
import { Employee } from '../data/Employee';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Issue } from '../data/Issue';

export default function ChangeSkillDialog({ issue, onSubmit, onCancel }: { issue: Issue, onSubmit: (changedSkill: Skill, preferredWorkers: Employee[]) => void, onCancel: () => void }) {

    const [skills, setSkills] = useState<Skill[]>([]);
    const [skillId, setSkillId] = useState(issue.RequiredSkillID);
    const [preferredWorkers, setPreferredWorkers] = useState<Employee[]>([]);
    const [skilledWorkers, setSkilledWorkers] = useState<Employee[]>([]);
    const [skillNotSpecified, setSkillNotSpecified] = useState(false);
    const [workersLoading, setWorkersLoading] = useState(true);
    
    const { api } = useRootStore();

    useEffect(() => {
        setWorkersLoading(true);
        api.getIssuePreferredWorkers(issue.ID)
            .then(workers => setPreferredWorkers(workers));

        api.getSkills().then(skills => {
            setSkills(skills);            
            if (skills.length == 1 && !skillId) {
                setSkillId(skills[0].ID);
            }
        });
    }, [issue, api]);

    const check = (): boolean => {
        let result = true;

        if (!skillId) {
            setSkillNotSpecified(true);
            result = false;
        }

        return result;
    }

    useEffect(() => {
        setWorkersLoading(true);
        if (!skillId) {
            setSkilledWorkers([]);            
            setPreferredWorkers([]);
            setWorkersLoading(false);
        } else {
            if (!issue.ProjectID) {
                setSkilledWorkers([]);
                setWorkersLoading(false);
            } else {
                api.getSkilledWorkers(skillId).then(sws => {
                    setSkilledWorkers(sws);
                    setWorkersLoading(false);
                });
            }
        }
    }, [api, skills, skillId]);

    const handleSkillIDChange = (newID: number) => {
        setSkillNotSpecified(false);
        setSkillId(newID);
    }

    const submit = async () => {
        if (check()) {
            await api.updateIssueSkillID(issue.ID, skillId!, preferredWorkers)
                .then(() => onSubmit({
                    ID: skillId!,
                    Name: skills.filter(x => x.ID == skillId)[0].Name
                }, preferredWorkers));
        }
    }

    const columns: GridColDef[] = [
        {
            field: 'ID', headerName: 'Исполнитель', width: 300, renderCell: (params) => <Link href={`/employees/${params.value}`}>{params.row.Name}</Link>
        },
        {
            field: 'IssuePreferredRate', headerName: 'Наценка', width: 120, align: 'right', valueFormatter: (value: number) => new Intl.NumberFormat("ru", { style: "percent" }).format(value / 100)
        },
    ];

    return (
        <Dialog
            maxWidth='md'            
            fullWidth
            open={true}
            onClose={onCancel}
        >
            <DialogTitle>Изменение направления</DialogTitle>
            <DialogContent sx={{ minHeight: '60vh' }}>
                <Box sx={{ mb: 3 }}>
                    <FormControl
                        sx={{ mt: 1, mr: 1, width: "100%", minWidth: 120 }}
                        error={skillNotSpecified}
                    >
                        <InputLabel>Направление</InputLabel>
                        <Select
                            label='Направление'
                            id="skill-id"
                            value={skillId ?? ''}
                            required
                            disabled={skills.length < 2}
                            onChange={(e) => handleSkillIDChange(e.target.value as number)}
                        >
                            {skills.map((s) => <MenuItem key={s.ID} value={s.ID}>{s.Name}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Box sx={{ mt: 3, pt:2, width: "100%", position: 'relative', borderRadius:1.2, border: '1px solid #cccccc' }}>
                        <label style={{background:'white', padding:'0px 15px 0px 7px'}} className="MuiFormLabel-root MuiInputLabel-root MuiInputLabel-formControl MuiInputLabel-shrink MuiInputLabel-sizeMedium MuiInputLabel-outlined MuiFormLabel-colorPrimary MuiFormLabel-filled MuiInputLabel-outlined css-113d811-MuiFormLabel-root-MuiInputLabel-root" data-shrink="true">Предпочтительные сотрудники</label>
                        <Box sx={{ pl: 3, pr: 3, pt: 1, pb: 1 }}>
                            {preferredWorkers.length == 0
                                ? "Нет выбранных сотрудников" :
                                <>
                                    Выбранные сотрудники: {preferredWorkers.map(w => <span><Link key={w.ID} href={`/employees/${w.ID}`}>{w.Name}</Link> </span>)}
                                </>
                            }
                        </Box>
                        {workersLoading
                            ? <Box sx={{ textAlign:'center', p:5 }}><CircularProgress /></Box> :
                            <DataGrid
                                onRowSelectionModelChange={(ids) => {
                                    setPreferredWorkers(skilledWorkers.filter(x => ids.includes(x.ID)));
                                    /*const selectedIDs = new Set(ids);
                                        const selectedRowData = rows.filter((row) =>
                                            selectedIDs.has(row.id.toString());
                                    );
                                    console.log(ids);*/
                                }}
                                density='compact'
                                hideFooter={skilledWorkers.length <= 100}
                                rows={skilledWorkers}
                                rowSelectionModel={preferredWorkers.map(x => x.ID)}
                                //getRowHeight={() => 'auto'}
                                getRowId={(row) => row.ID}
                                columns={columns}
                                //initialState={{ pagination: { paginationModel } }}
                                //pageSizeOptions={[5, 10]}
                                checkboxSelection
                                sx={{ border: 0, m: 1 }}
                            />
                        }
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ pr: 3} }>
                <Button onClick={onCancel}>Отмена</Button>
                <Button onClick={submit}>Сохранить</Button>
            </DialogActions>
        </Dialog>
    );
}